import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dataApi } from '../../services/api';
import * as appwriteService from '../../services/appwrite';
import { COLLECTIONS } from '../../services/appwrite';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, ArrowUpDown, ExternalLink, RefreshCw, Search, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Checkbox } from '../../components/ui/checkbox';
import { Input } from '../../components/ui/input';
import {
  PANEL_QUOTE_COLUMNS,
  NO_SORT,
  filterAndSortQuotes,
  nextSort,
  searchTerms as parseSearchTerms
} from '../../lib/panelQuoteJobs';

// A sync whose lastSyncStarted is older than this is considered dead
// (the function hard-timeout is 120s) and no longer blocks manual refresh.
const SYNC_STALE_MS = 5 * 60 * 1000;
// Auto-run a sync when the dialog opens and the cache is older than this.
const AUTO_SYNC_MAX_AGE_MS = 24 * 60 * 60 * 1000;
// Re-enable the refresh button if the async execution never reports back.
const SYNC_REQUEST_TIMEOUT_MS = 45 * 1000;

const ImportPanelQuoteForm = ({ isOpen, onClose, onSuccess, onError }) => {
  const [loadingInitial, setLoadingInitial] = useState(false);
  const [importing, setImporting] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [statusDoc, setStatusDoc] = useState(null);
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [errorMessage, setErrorMessage] = useState('');
  const [teamId, setTeamId] = useState(null);
  // Optimistic "sync requested" flag so the button disables immediately,
  // before the function cold-starts and flips panel-quote-status.syncing.
  const [syncRequested, setSyncRequested] = useState(false);
  const [search, setSearch] = useState('');
  // key: null keeps the order Panel Quote listed the jobs in (sortOrder).
  const [sort, setSort] = useState(NO_SORT);

  const autoSyncTriggeredRef = useRef(false);
  const syncRequestTimerRef = useRef(null);
  const statusUpdatedAtRef = useRef(null);
  // Parent components pass inline handlers whose identity changes per render;
  // route through a ref so the dialog-open effect doesn't reset mid-use.
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // Not memoised: depends on Date.now(), and the tick below re-renders while a
  // sync is running so a dead sync stops blocking the refresh button.
  const syncStartedAt = statusDoc?.lastSyncStarted ? new Date(statusDoc.lastSyncStarted).getTime() : 0;
  const isSyncing = syncRequested
    || (!!statusDoc?.syncing && !!syncStartedAt && Date.now() - syncStartedAt < SYNC_STALE_MS);

  const [, setStaleTick] = useState(0);
  useEffect(() => {
    if (!statusDoc?.syncing) return undefined;
    const interval = setInterval(() => setStaleTick((t) => t + 1), 30 * 1000);
    return () => clearInterval(interval);
  }, [statusDoc?.syncing]);

  const terms = useMemo(() => parseSearchTerms(search), [search]);

  const visibleQuotes = useMemo(
    () => filterAndSortQuotes(quotes, { terms, sort }),
    [quotes, terms, sort]
  );

  const selectableQuotes = useMemo(
    () => visibleQuotes.filter((quote) => !quote.quoteRequestId),
    [visibleQuotes]
  );

  const allSelected = useMemo(
    () => selectableQuotes.length > 0
      && selectableQuotes.every((quote) => selectedJobs.has(quote.panelQuoteJobId)),
    [selectableQuotes, selectedJobs]
  );

  const countLabel = terms.length > 0
    ? `${visibleQuotes.length} of ${quotes.length} jobs`
    : `${quotes.length} ${quotes.length === 1 ? 'job' : 'jobs'}`;

  const lastSyncedLabel = useMemo(() => {
    if (!statusDoc?.quotesLastSynced) return 'Never synced';
    try {
      return `Last synced ${formatDistanceToNow(new Date(statusDoc.quotesLastSynced), { addSuffix: true })}`;
    } catch {
      return 'Last synced recently';
    }
  }, [statusDoc?.quotesLastSynced]);

  const toggleJobSelection = (panelQuoteJobId) => {
    const quote = quotes.find((q) => q.panelQuoteJobId === panelQuoteJobId);
    if (quote?.quoteRequestId) return;
    setSelectedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(panelQuoteJobId)) {
        next.delete(panelQuoteJobId);
      } else {
        next.add(panelQuoteJobId);
      }
      return next;
    });
  };

  // Select-all applies to the rows currently on screen, and leaves anything
  // selected under an earlier search alone — selections survive the filter so
  // several searches can be gathered into one import.
  const toggleSelectAll = () => {
    setSelectedJobs((prev) => {
      const next = new Set(prev);
      selectableQuotes.forEach((quote) => {
        if (allSelected) {
          next.delete(quote.panelQuoteJobId);
        } else {
          next.add(quote.panelQuoteJobId);
        }
      });
      return next;
    });
  };

  const toggleSort = (column) => setSort((prev) => nextSort(prev, column));

  const triggerSync = useCallback(async ({ auto = false } = {}) => {
    setSyncRequested(true);
    if (syncRequestTimerRef.current) clearTimeout(syncRequestTimerRef.current);
    syncRequestTimerRef.current = setTimeout(() => setSyncRequested(false), SYNC_REQUEST_TIMEOUT_MS);
    try {
      await dataApi.panelQuoteSyncQuotes();
    } catch (err) {
      console.error('Error starting Panel Quote sync:', err);
      setSyncRequested(false);
      if (syncRequestTimerRef.current) clearTimeout(syncRequestTimerRef.current);
      if (!auto) toast.error(err.message || 'Failed to start Panel Quote sync');
    }
  }, []);

  const handleRefreshClick = () => {
    if (isSyncing || importing) return;
    triggerSync();
  };

  // Load cached quotes + sync status when the dialog opens, then decide
  // whether the cache is stale enough to auto-run a sync.
  useEffect(() => {
    if (!isOpen) {
      autoSyncTriggeredRef.current = false;
      return undefined;
    }

    let cancelled = false;
    setSelectedJobs(new Set());
    setQuotes([]);
    setStatusDoc(null);
    setErrorMessage('');
    setSyncRequested(false);
    setSearch('');
    setSort(NO_SORT);
    setLoadingInitial(true);
    statusUpdatedAtRef.current = null;

    (async () => {
      try {
        const [settings, user] = await Promise.all([
          dataApi.panelQuoteSettingsGet(),
          appwriteService.getCurrentUser()
        ]);
        if (cancelled) return;

        if (!user?.teamId) {
          throw new Error('No team selected');
        }
        setTeamId(user.teamId);

        if (!settings?.enabled || !settings?.hasPassword) {
          throw new Error('Panel Quote credentials are not configured. Add them in Settings first.');
        }

        const [cachedQuotes, syncStatus] = await Promise.all([
          dataApi.panelQuoteCachedQuotes(),
          dataApi.panelQuoteSyncStatusGet()
        ]);
        if (cancelled) return;

        setQuotes(cachedQuotes);
        setStatusDoc(syncStatus);
        statusUpdatedAtRef.current = syncStatus?.$updatedAt || null;

        // Auto-sync when the cache is missing or older than a day
        if (!autoSyncTriggeredRef.current) {
          autoSyncTriggeredRef.current = true;
          const lastSynced = syncStatus?.quotesLastSynced
            ? new Date(syncStatus.quotesLastSynced).getTime()
            : 0;
          const startedAt = syncStatus?.lastSyncStarted
            ? new Date(syncStatus.lastSyncStarted).getTime()
            : 0;
          const syncAlreadyRunning = !!syncStatus?.syncing
            && !!startedAt
            && Date.now() - startedAt < SYNC_STALE_MS;
          const cacheIsStale = !lastSynced || Date.now() - lastSynced > AUTO_SYNC_MAX_AGE_MS;
          if (!syncAlreadyRunning && cacheIsStale) {
            triggerSync({ auto: true });
          }
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading Panel Quote quotes:', err);
        const message = err.message || 'Failed to load Panel Quote quotes';
        setErrorMessage(message);
        if (onErrorRef.current) onErrorRef.current(message);
      } finally {
        if (!cancelled) setLoadingInitial(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, triggerSync]);

  // Realtime: keep the cached list and sync status live while the dialog is
  // open. Row updates replace items in place so the current selection is not
  // interrupted; selections are only pruned when a row is removed or becomes
  // imported.
  useEffect(() => {
    if (!isOpen || !teamId) return undefined;

    let unsubscribeQuotes = () => {};
    let unsubscribeStatus = () => {};

    try {
      unsubscribeQuotes = appwriteService.subscribeToCollection(
        COLLECTIONS.PANEL_QUOTE_QUOTES,
        (response) => {
          const doc = response.payload;
          if (!doc || doc.organisationId !== teamId) return;
          const isDelete = (response.events || []).some((e) => e.includes('.delete'));
          const isActive = !isDelete && doc.status === 'active';

          setQuotes((prev) => {
            const idx = prev.findIndex((q) => q.$id === doc.$id);
            if (!isActive) {
              return idx >= 0 ? prev.filter((q) => q.$id !== doc.$id) : prev;
            }
            const next = idx >= 0 ? [...prev] : [...prev, doc];
            if (idx >= 0) next[idx] = doc;
            next.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
            return next;
          });

          if (!isActive || doc.quoteRequestId) {
            setSelectedJobs((prev) => {
              if (!prev.has(doc.panelQuoteJobId)) return prev;
              const next = new Set(prev);
              next.delete(doc.panelQuoteJobId);
              return next;
            });
          }
        }
      );
    } catch (err) {
      console.warn('[Realtime] Panel Quote quotes subscription failed:', err.message);
    }

    unsubscribeStatus = appwriteService.subscribeToDocument(
      COLLECTIONS.PANEL_QUOTE_STATUS,
      teamId,
      (response) => {
        if (response.payload) setStatusDoc(response.payload);
      }
    );

    return () => {
      if (typeof unsubscribeQuotes === 'function') unsubscribeQuotes();
      if (typeof unsubscribeStatus === 'function') unsubscribeStatus();
    };
  }, [isOpen, teamId]);

  // Any server-side write to the status row supersedes the optimistic
  // "sync requested" flag.
  useEffect(() => {
    const updatedAt = statusDoc?.$updatedAt || null;
    if (statusUpdatedAtRef.current && updatedAt && updatedAt !== statusUpdatedAtRef.current) {
      setSyncRequested(false);
      if (syncRequestTimerRef.current) clearTimeout(syncRequestTimerRef.current);
    }
    if (updatedAt) statusUpdatedAtRef.current = updatedAt;
  }, [statusDoc]);

  useEffect(() => () => {
    if (syncRequestTimerRef.current) clearTimeout(syncRequestTimerRef.current);
  }, []);

  const handleImport = async () => {
    if (selectedJobs.size === 0) {
      toast.error('Select at least one Panel Quote job to import');
      return;
    }

    setImporting(true);
    const jobsToImport = quotes.filter((quote) => selectedJobs.has(quote.panelQuoteJobId));

    try {
      // One execution for the whole selection: the function imports the jobs
      // one at a time. Starting one execution per job overloaded Panel Quote.
      await dataApi.panelQuoteImportBatch(jobsToImport.map((quote) => quote.panelQuoteJobId));

      const jobWord = jobsToImport.length === 1 ? 'job' : 'jobs';
      const message = `Import started for ${jobsToImport.length} ${jobWord}. They will appear on the dashboard shortly.`;
      if (onSuccess) onSuccess({ message });
      onClose();
    } catch (err) {
      console.error('Error importing Panel Quote jobs:', err);
      const message = err.message || 'Failed to import jobs from Panel Quote';
      if (onError) onError(message);
    } finally {
      setImporting(false);
    }
  };

  const renderSkeletonRows = () => (
    <>
      {Array.from({ length: 3 }).map((_, idx) => (
        <tr key={`skeleton-${idx}`} className="border-b">
          <td colSpan={PANEL_QUOTE_COLUMNS.length} className="p-4">
            <div className="space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          </td>
        </tr>
      ))}
    </>
  );

  const renderSortButton = (column) => {
    const isActive = sort.key === column.sortKey;
    const Icon = isActive ? (sort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
    return (
      <button
        type="button"
        onClick={() => toggleSort(column)}
        title={`Sort by ${column.label.toLowerCase()}`}
        className={`group inline-flex items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 ${column.align === 'right' ? 'flex-row-reverse' : ''}`}
      >
        <span>{column.label}</span>
        <Icon
          className={`h-3 w-3 transition-opacity ${isActive ? 'opacity-100' : 'opacity-30 group-hover:opacity-70'}`}
        />
      </button>
    );
  };

  const renderQuoteRow = (quote) => {
    const isSelected = selectedJobs.has(quote.panelQuoteJobId);
    const isImported = !!quote.quoteRequestId;
    const hasExcessAmount = quote.amountExcess > 0;

    return (
      <tr
        key={quote.$id}
        data-pq-id={quote.panelQuoteJobId}
        className={`border-b transition-colors ${isImported ? 'opacity-60 bg-muted/30' : isSelected ? 'bg-muted/60 cursor-pointer' : 'hover:bg-muted/40 cursor-pointer'}`}
        onClick={() => !isImported && toggleJobSelection(quote.panelQuoteJobId)}
      >
        <td className="py-3 px-3 align-top">
          <div className="flex items-start gap-3">
            {isImported ? (
              <a
                href={`/quote-requests/${quote.quoteRequestId}`}
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline whitespace-nowrap"
                title="Open"
              >
                <ExternalLink className="h-3 w-3" />
                Imported
              </a>
            ) : (
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => toggleJobSelection(quote.panelQuoteJobId)}
                onClick={(e) => e.stopPropagation()}
              />
            )}
            <div className="space-y-1">
              <div className="font-semibold leading-tight">{quote.jobNumber || '—'}</div>
              {quote.claimNumber && (
                <div className="text-xs text-muted-foreground">Claim: {quote.claimNumber}</div>
              )}
            </div>
          </div>
        </td>

        <td className="py-3 px-3 align-top text-sm">
          <div className="font-medium">{quote.customerName || '—'}</div>
          {quote.customerSecondary && (
            <div className="text-muted-foreground">{quote.customerSecondary}</div>
          )}
        </td>

        <td className="py-3 px-3 align-top text-sm">
          <div className="font-medium break-words">{quote.vehicle || '—'}</div>
        </td>

        <td className="py-3 px-3 align-top text-sm text-muted-foreground">{quote.jobStatus || '—'}</td>
        <td className="py-3 px-3 align-top text-sm font-semibold whitespace-nowrap">{quote.numPhotos ?? 0}</td>
        <td className="py-3 px-3 align-top text-sm text-muted-foreground whitespace-nowrap">{quote.date || '—'}</td>

        <td className="py-3 px-3 align-top text-sm font-semibold text-right">
          <div>${(quote.amountPrimary ?? 0).toFixed(2)}</div>
          {hasExcessAmount && (
            <div className="text-xs text-muted-foreground">Excess ${quote.amountExcess.toFixed(2)}</div>
          )}
        </td>
      </tr>
    );
  };

  const showSkeletons = loadingInitial || (quotes.length === 0 && isSyncing && !errorMessage);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl w-[1120px] h-[90vh] flex flex-col animate-none">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-3">
            <DialogTitle>Import from Panel Quote</DialogTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={handleRefreshClick}
              disabled={isSyncing || importing || loadingInitial || !!errorMessage}
              title={isSyncing ? 'Sync in progress' : 'Refresh from Panel Quote'}
            >
              <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            </Button>
            <span className="text-xs text-muted-foreground">
              {isSyncing ? 'Syncing with Panel Quote…' : lastSyncedLabel}
            </span>
          </div>
        </DialogHeader>

        <div className="flex-shrink-0 flex items-center gap-3">
          <div className="relative w-80">
            <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search job, customer or vehicle…"
              aria-label="Search Panel Quote jobs"
              className="pl-8 pr-8 h-9"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {!loadingInitial && !errorMessage && (
            <span className="text-xs text-muted-foreground">{countLabel}</span>
          )}

          {selectedJobs.size > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{selectedJobs.size} selected</span>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setSelectedJobs(new Set())}
              >
                Clear
              </Button>
            </div>
          )}
        </div>

        <div className="flex-1 min-h-[20rem] overflow-y-auto space-y-4 pr-1">
          {errorMessage && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
              {errorMessage}
            </div>
          )}

          {!errorMessage && !isSyncing && statusDoc?.lastSyncStatus === 'error' && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
              Last sync failed{statusDoc.lastSyncError ? `: ${statusDoc.lastSyncError}` : ''}. Showing previously cached quotes.
            </div>
          )}

          <div className="border rounded-md overflow-clip">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-background text-xs font-semibold uppercase tracking-wide">
                <tr>
                  {PANEL_QUOTE_COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      aria-sort={sort.key === col.sortKey
                        ? (sort.direction === 'asc' ? 'ascending' : 'descending')
                        : 'none'}
                      className={`bg-muted/50 py-2 px-3 text-left ${col.align === 'right' ? 'text-right' : ''}`}
                    >
                      {col.key === 'select' ? (
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={allSelected}
                            disabled={loadingInitial || selectableQuotes.length === 0}
                            onCheckedChange={toggleSelectAll}
                            aria-label="Select all jobs shown"
                          />
                          {renderSortButton(col)}
                        </div>
                      ) : (
                        renderSortButton(col)
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="align-top">
                {showSkeletons ? (
                  renderSkeletonRows()
                ) : quotes.length === 0 ? (
                  <tr>
                    <td colSpan={PANEL_QUOTE_COLUMNS.length} className="p-6 text-center text-muted-foreground">
                      No quotes available.
                    </td>
                  </tr>
                ) : visibleQuotes.length === 0 ? (
                  <tr>
                    <td colSpan={PANEL_QUOTE_COLUMNS.length} className="p-6 text-center text-muted-foreground">
                      No jobs match “{search.trim()}”.{' '}
                      <button
                        type="button"
                        onClick={() => setSearch('')}
                        className="text-primary hover:underline"
                      >
                        Clear search
                      </button>
                    </td>
                  </tr>
                ) : (
                  visibleQuotes.map(renderQuoteRow)
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex-shrink-0 border-t pt-4 flex justify-end gap-2">
          <Button
            onClick={handleImport}
            disabled={importing || selectedJobs.size === 0 || loadingInitial || !!errorMessage}
          >
            {importing ? 'Importing…' : `Import ${selectedJobs.size || ''}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportPanelQuoteForm;
